import React, { useState } from 'react';
import TopNav from '../components/trend/TopNav';
import AnomalyPanel from '../components/trend/AnomalyPanel';
import DailyTrendChart from '../components/trend/DailyTrendChart';
import RangeTrendCandleChart from '../components/trend/RangeTrendCandleChart';
import MultiStationTrendChart from '../components/trend/MultiStationTrendChart';
import FilterPanel from '../components/trend/FilterPanel';
import InsightCards from '../components/trend/InsightCards';
import WeatherTable from '../components/trend/WeatherTable';
import YearlyTrendChart from '../components/trend/YearlyTrendChart';
import useTrends from '../hooks/useTrends';
import { ToggleButton, ToggleButtonGroup, Box, Tooltip } from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import { downloadTrends } from '../components/trend/DownloadTrends';
// ... other imports
function TrendPage() {
    const {
        dailyTrends,
        yearlyTrends,
        clusterInsights,
        anomalies,
        modelVersion,
        loading,
        error,
        fetchTrends,
        fullData
    } = useTrends();

    const [selectedYear] = useState(new Date().getFullYear());

    // We still keep filters local to know which view (Daily/Yearly) to show
    const [filters, setFilters] = useState(null);
    const [chartType, setChartType] = useState('line');
    const [downloadFormat, setDownloadFormat] = useState("csv");

    const handleChartTypeChange = (event, newChartType) => {
        if (newChartType !== null) {
            setChartType(newChartType);
        }
    };

    const handleFetchTrends = async (filterData) => {
        setFilters(filterData);

        // BUSINESS RULE: Reset chart type to 'line' if:
        // 1. Switching away from 'Range' while in 'candle' mode
        // 2. OR In 'Range' mode but Multiple Stations are selected (Candle not supported)
        if (
            (filterData.trendType !== 'Range' && chartType === 'candle') ||
            (filterData.trendType === 'Range' && filterData.station?.length > 1 && chartType === 'candle')
        ) {
            setChartType('line');
        }

        // Call the hook's fetch function
        // ... rest of fetch logic
        const date = filterData.date ? filterData.date : new Date();
        const locations = filterData.station || [];
        const dateRange = {
            fromDate: filterData.fromDate,
            toDate: filterData.toDate
        };

        console.log("📤 Sending request to backend:", {
            date,
            locations,
            trendType: filterData.trendType,
            dateRange
        });

        try {
            await fetchTrends(date, locations, filterData.trendType, dateRange);
        } catch (err) {
            console.error("View: Fetch failed", err);
        }
    };
    const handleDownload = () => {
        if (!filters) return;

        const dataToDownload =
            filters.trendType === 'Yearly'
                ? yearlyTrends?.records
                : dailyTrends?.records;

        if (!dataToDownload || dataToDownload.length === 0) {
            console.warn("No data available for download");
            return;
        }

        downloadTrends(dataToDownload, downloadFormat);
    };
    const isDaily = filters?.trendType !== 'Yearly';

    // Custom styling for ToggleButton to match requirements
    const toggleBtnSx = {
        textTransform: 'none',
        padding: '6px 16px',
        fontWeight: 500,
        borderColor: 'rgba(0, 0, 0, 0.12)',
        color: 'text.secondary',
        '&.Mui-selected': {
            backgroundColor: 'primary.light', // Light background for active
            color: 'primary.dark', // Text/Icon color for active
            fontWeight: 700,
            borderColor: 'primary.main',
            '&:hover': {
                backgroundColor: 'primary.light',
            }
        }
    };

    return (
        <>
            <TopNav version={modelVersion} />
            <FilterPanel
                onFetch={handleFetchTrends}
                loading={loading}
                onDownload={handleDownload}
                downloadFormat={downloadFormat}
                setDownloadFormat={setDownloadFormat}
            />
            {/* Error Message Display */}
            {error && (
                <div style={{ color: 'red', padding: '1rem', textAlign: 'center', background: '#ffebee', margin: '1rem' }}>
                    <strong>Error:</strong> {error}
                </div>
            )}

            {/* Conditional Rendering */}
            {fullData && console.log("🔥 Backend Data:", fullData)}
            {fullData ? (
                <div className="grid-layout fade-in" style={{ gridAutoRows: 'auto' }}>

                    {/* Row 1: Charts Section - Full width */}
                    <div style={{ gridColumn: 'span 12', gridRow: '1', minHeight: '400px', marginBottom: '1.5rem' }}>
                        {/* Graph Type Selector - Available for Daily, Weekly, and Range */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: 2 }}>
                            <ToggleButtonGroup
                                value={chartType}
                                exclusive
                                onChange={handleChartTypeChange}
                                aria-label="chart type"
                                size="small"
                                sx={{
                                    backgroundColor: 'background.paper',
                                    boxShadow: 1
                                }}
                            >
                                <ToggleButton value="line" aria-label="line chart" sx={toggleBtnSx}>
                                    <ShowChartIcon sx={{ mr: 1 }} /> Line
                                </ToggleButton>
                                <ToggleButton value="bar" aria-label="bar chart" sx={toggleBtnSx}>
                                    <BarChartIcon sx={{ mr: 1 }} /> Bar
                                </ToggleButton>
                                {filters?.trendType === 'Range' && (
                                    <Tooltip title={filters?.station?.length > 1 ? "Candle chart is available only for single station selection." : ""}>
                                        <span style={{ display: 'inline-block' }}>
                                            <ToggleButton
                                                value="candle"
                                                aria-label="candle chart"
                                                sx={toggleBtnSx}
                                                disabled={filters?.station?.length > 1}
                                            >
                                                <CandlestickChartIcon sx={{ mr: 1 }} /> Candle
                                            </ToggleButton>
                                        </span>
                                    </Tooltip>
                                )}
                            </ToggleButtonGroup>
                        </Box>

                        {/* Chart Rendering Logic */}
                        {/* Chart Rendering Logic */}
                        {filters?.trendType === 'Range' ? (
                            chartType === 'candle' ? (
                                <RangeTrendCandleChart
                                    data={dailyTrends?.records || []}
                                    loading={loading}
                                    selectedStations={filters?.station || []}
                                />
                            ) : (filters?.station && filters.station.length > 1) ? (
                                <MultiStationTrendChart
                                    data={dailyTrends?.records || []}
                                    loading={loading}
                                    chartType={chartType}
                                    selectedStations={filters?.station || []}
                                    trendType={filters?.trendType}
                                />
                            ) : (
                                <DailyTrendChart
                                    data={dailyTrends?.records || []}
                                    loading={loading}
                                    chartType={chartType}
                                    trendType={filters?.trendType}
                                />
                            )
                        ) : filters?.trendType === 'Yearly' ? (
                            <YearlyTrendChart
                                data={yearlyTrends.records}
                                loading={loading}
                                selectedYear={selectedYear}
                                chartType={chartType}
                            />
                        ) : (filters?.station && filters.station.length > 1) ? (
                            <MultiStationTrendChart
                                data={dailyTrends?.records || []}
                                loading={loading}
                                chartType={chartType}
                                selectedStations={filters?.station || []}
                                trendType={filters?.trendType}
                            />
                        ) : (
                            <DailyTrendChart
                                data={dailyTrends?.records || []}
                                loading={loading}
                                chartType={chartType}
                                trendType={filters?.trendType}
                            />
                        )}
                    </div>

                    {/* Row 2: Table - Full width */}
                    <div style={{ gridColumn: 'span 12', gridRow: '2', minHeight: '300px', marginBottom: '1.5rem' }}>
                        <WeatherTable
                            data={
                                filters?.trendType === "Yearly"
                                    ? yearlyTrends?.records || []
                                    : dailyTrends?.records || []
                            }
                            loading={loading}
                        />

                    </div>

                    {/* Row 3: Insights and Anomalies side by side (above footer) */}
                    <div style={{
                        gridColumn: 'span 12',
                        gridRow: '3',
                        display: 'flex',
                        gap: '1.5rem',
                        minHeight: '300px'
                    }}>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <InsightCards
                                clusterInsights={clusterInsights}
                                loading={loading}
                            />
                        </div>

                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <AnomalyPanel
                                anomalies={anomalies}
                                loading={loading}
                            />
                        </div>
                    </div>

                </div>
            ) : (
                /* Welcome Placeholder when no data is selected */
                <div className="flex-center" style={{ flexDirection: 'column', height: '50vh', textAlign: 'center', color: 'var(--text-primary)', opacity: 0.8 }}>
                    <div style={{
                        background: 'var(--bg-paper)',
                        padding: '2rem 3rem',
                        borderRadius: '24px',
                        boxShadow: 'var(--card-shadow)',
                        maxWidth: '500px'
                    }}>
                        <h2 style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--primary-main)', marginBottom: '1rem' }}>
                            Explore Weather Patterns
                        </h2>
                        <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                            Select a station and date above to visualize daily trends, anomalies, and cluster insights.
                            <br />
                            <span style={{ fontSize: '0.9rem', marginTop: '1rem', display: 'block', color: 'var(--secondary-main)', fontWeight: 600 }}>
                                Ready to analyze?
                            </span>
                        </p>
                    </div>
                </div>
            )}
        </>
    );
}
export default TrendPage;